import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { getStoredUser } from "../lib/auth";
import { useLanguage } from "../lib/language";
import { repairMojibakeDeep, repairMojibakeText } from "../lib/text";

const QUESTION_TYPES = ["mcq", "essay", "true_false", "rating"];

const createQuestionDraft = (index = 0) => ({
  question_text: "",
  question_type: "mcq",
  points: 1,
  question_order: index + 1,
  options: [
    { option_text: "", is_correct: true },
    { option_text: "", is_correct: false }
  ]
});

const createCourseDraft = () => ({
  title: "",
  description: "",
  assessmentTitle: "",
  show_results_to_student: false,
  show_answers_to_student: false,
  show_ai_report_to_student: false,
  questions: [createQuestionDraft(0)]
});

const copyMap = {
  ar: {
    eyebrow: "الكورسات",
    title: "إدارة الكورسات والتقييمات",
    subtitle: "أنشئ كورسًا كاملًا بالأسئلة، أضف تقييمات يدوية عند الحاجة، واعرض أكواد الطلاب لكل كورس بسهولة.",
    totalCourses: "إجمالي الكورسات",
    totalAssessments: "إجمالي التقييمات",
    totalSubmissions: "إجمالي التسليمات",
    loading: "جارٍ تحميل الكورسات...",
    empty: "لا توجد كورسات بعد.",
    openDashboard: "فتح لوحة المدرب",
    openAiStudio: "فتح استوديو الذكاء الاصطناعي",
    chooseCourse: "اختر كورسًا",
    selectedCourse: "الكورس الحالي",
    assessments: "التقييمات",
    submissions: "التسليمات",
    showScore: "السماح للطالب برؤية الدرجة",
    showAnswers: "السماح للطالب بمراجعة الإجابات",
    showAi: "السماح للطالب برؤية تقرير الذكاء الاصطناعي",
    creating: "جارٍ الإنشاء...",
    loadAssessments: "جارٍ تحميل تقييمات الكورس...",
    noAssessments: "لا توجد تقييمات لهذا الكورس حتى الآن.",
    assessmentPairs: "مجموعات التقييمات",
    baseCode: "الكود الأساسي",
    preCode: "كود القبلي",
    postCode: "كود البعدي",
    questions: "الأسئلة",
    copied: "تم النسخ بنجاح",
    copy: "نسخ",
    createError: "تعذر إنشاء التقييم الآن",
    courseCodeHint: "هذا هو الكود الأساسي الذي يجمع القبلي والبعدي معًا.",
    fullCourseTitle: "إنشاء كورس كامل بالأسئلة",
    fullCourseSubtitle: "سننشئ الكورس، ثم مجموعة تقييم قبلي وبعدي، ثم نضيف الأسئلة تلقائيًا داخل نفس التدفق.",
    courseTitle: "اسم الكورس",
    courseTitlePlaceholder: "مثال: مقدمة في المهارات الخضراء",
    courseDescription: "وصف الكورس",
    courseDescriptionPlaceholder: "وصف مختصر يساعدك على تمييز الكورس داخل المنصة.",
    pairTitle: "اسم مجموعة التقييم",
    pairTitlePlaceholder: "مثال: تقييم البداية والنهاية",
    addQuestion: "إضافة سؤال",
    removeQuestion: "حذف السؤال",
    questionLabel: "السؤال",
    questionText: "نص السؤال",
    questionTextPlaceholder: "اكتب السؤال هنا",
    questionType: "نوع السؤال",
    questionPoints: "الدرجة",
    questionRequired: "السؤال إجباري",
    optionText: "نص الاختيار",
    addOption: "إضافة اختيار",
    removeOption: "حذف الاختيار",
    correctOption: "إجابة صحيحة",
    ratingHelp: "أسئلة التقييم من 1 إلى 10 يتم تجهيز اختياراتها تلقائيًا.",
    trueFalseHelp: "صواب / خطأ يتم تجهيزهما تلقائيًا.",
    createFullCourse: "إنشاء الكورس كاملًا",
    fullCourseCreated: "تم إنشاء الكورس والتقييمات والأسئلة بنجاح",
    noCourseSelected: "اختر كورسًا أولًا",
    existingCourses: "الكورسات الحالية",
    deleteCourse: "حذف الكورس",
    deleteCourseConfirm: "سيتم حذف الكورس وكل تقييماته وأسئلته وتسليماته. هل تريد المتابعة؟",
    deleteSuccess: "تم حذف الكورس بنجاح",
    deleteError: "تعذر حذف الكورس الآن",
    courseDetails: "تفاصيل الكورس",
    assessmentCount: "عدد التقييمات",
    submissionCount: "عدد التسليمات",
    viewCodes: "أكواد الطلاب",
    type_mcq: "اختيار من متعدد",
    type_essay: "مقالي",
    type_true_false: "صح أو خطأ",
    type_rating: "تقييم من 1 إلى 10",
    copyBaseCode: "نسخ الكود الأساسي",
    copyPreCode: "نسخ كود القبلي",
    copyPostCode: "نسخ كود البعدي",
    courseCreatedSelectHint: "تم اختيار الكورس الجديد تلقائيًا لتكمل عليه فورًا."
  },
  en: {
    eyebrow: "Courses",
    title: "Course And Assessment Management",
    subtitle: "Create a full course with questions, add manual assessments when needed, and view student-facing codes for each course.",
    totalCourses: "Total Courses",
    totalAssessments: "Total Assessments",
    totalSubmissions: "Total Submissions",
    loading: "Loading courses...",
    empty: "There are no courses yet.",
    openDashboard: "Open Trainer Dashboard",
    openAiStudio: "Open AI Studio",
    chooseCourse: "Choose a course",
    selectedCourse: "Current Course",
    assessments: "Assessments",
    submissions: "Submissions",
    showScore: "Allow the student to see the score",
    showAnswers: "Allow the student to review answers",
    showAi: "Allow the student to see the AI report",
    creating: "Creating...",
    loadAssessments: "Loading course assessments...",
    noAssessments: "There are no assessments for this course yet.",
    assessmentPairs: "Assessment Pairs",
    baseCode: "Base Code",
    preCode: "Pre Code",
    postCode: "Post Code",
    questions: "Questions",
    copied: "Copied successfully",
    copy: "Copy",
    createError: "Unable to create the assessment right now",
    courseCodeHint: "This base code groups the pre and post assessments together.",
    fullCourseTitle: "Create A Full Course With Questions",
    fullCourseSubtitle: "We will create the course, then the pre/post assessment pair, then attach the questions automatically.",
    courseTitle: "Course Title",
    courseTitlePlaceholder: "Example: Introduction To Green Skills",
    courseDescription: "Course Description",
    courseDescriptionPlaceholder: "A short description to help you identify the course.",
    pairTitle: "Assessment Pair Title",
    pairTitlePlaceholder: "Example: Entry And Exit Assessment",
    addQuestion: "Add Question",
    removeQuestion: "Remove Question",
    questionLabel: "Question",
    questionText: "Question Text",
    questionTextPlaceholder: "Write the question here",
    questionType: "Question Type",
    questionPoints: "Points",
    questionRequired: "Required Question",
    optionText: "Option Text",
    addOption: "Add Option",
    removeOption: "Remove Option",
    correctOption: "Correct Answer",
    ratingHelp: "Rating questions from 1 to 10 are prepared automatically.",
    trueFalseHelp: "True / False options are prepared automatically.",
    createFullCourse: "Create Full Course",
    fullCourseCreated: "The course, assessments, and questions were created successfully",
    noCourseSelected: "Choose a course first",
    existingCourses: "Current Courses",
    deleteCourse: "Delete Course",
    deleteCourseConfirm: "This will delete the course and all related assessments, questions, and submissions. Continue?",
    deleteSuccess: "Course deleted successfully",
    deleteError: "Unable to delete the course right now",
    courseDetails: "Course Details",
    assessmentCount: "Assessment Count",
    submissionCount: "Submission Count",
    viewCodes: "Student Codes",
    type_mcq: "Multiple Choice",
    type_essay: "Essay",
    type_true_false: "True Or False",
    type_rating: "Rating From 1 To 10",
    copyBaseCode: "Copy Base Code",
    copyPreCode: "Copy Pre Code",
    copyPostCode: "Copy Post Code",
    courseCreatedSelectHint: "The new course was selected automatically so you can continue working on it."
  }
};

function buildOptionsForQuestion(question) {
  if (question.question_type === "rating") {
    return Array.from({ length: 10 }, (_, index) => ({
      option_text: String(index + 1),
      is_correct: false
    }));
  }

  if (question.question_type === "true_false") {
    return [
      { option_text: "صح", is_correct: true },
      { option_text: "خطأ", is_correct: false }
    ];
  }

  if (question.question_type === "mcq") {
    return (question.options || [])
      .map((option) => ({
        option_text: option.option_text.trim(),
        is_correct: Boolean(option.is_correct)
      }))
      .filter((option) => option.option_text);
  }

  return [];
}

// صفحة إدارة الكورسات للمدرب: إنشاء كورس كامل بالأسئلة، عرض الأكواد، وحذف الكورسات عند الحاجة.
function TrainerCoursesPage() {
  const user = getStoredUser();
  const navigate = useNavigate();
  const { language } = useLanguage();
  const isArabic = language === "ar";
  const copy = repairMojibakeDeep(copyMap[isArabic ? "ar" : "en"]);

  const [courses, setCourses] = useState([]);
  const [selectedCourseId, setSelectedCourseId] = useState("");
  const [assessments, setAssessments] = useState([]);
  const [questionBank, setQuestionBank] = useState([]);
  const [courseForm, setCourseForm] = useState(createCourseDraft);
  const [feedback, setFeedback] = useState("");
  const [feedbackType, setFeedbackType] = useState("info");
  const [isLoadingCourses, setIsLoadingCourses] = useState(true);
  const [isLoadingAssessments, setIsLoadingAssessments] = useState(false);
  const [isLoadingQuestionBank, setIsLoadingQuestionBank] = useState(false);
  const [isCreatingCourse, setIsCreatingCourse] = useState(false);
  const [deletingCourseId, setDeletingCourseId] = useState(null);
  const [coursesVersion, setCoursesVersion] = useState(0);
  const [isQuestionBankOpen, setIsQuestionBankOpen] = useState(false);

  useEffect(() => {
    let isMounted = true;

    async function fetchCourses() {
      if (!user?.id) {
        setIsLoadingCourses(false);
        return;
      }

      try {
        setIsLoadingCourses(true);
        const response = await api.get(`/trainer/${user.id}/courses`);
        if (!isMounted) {
          return;
        }
        const nextCourses = repairMojibakeDeep(response.data.courses || []);
        setCourses(nextCourses);
        setSelectedCourseId((currentValue) => {
          if (currentValue && nextCourses.some((course) => String(course.id) === String(currentValue))) {
            return currentValue;
          }
          return nextCourses.length ? String(nextCourses[0].id) : "";
        });
      } catch (error) {
        if (isMounted) {
          setFeedbackType("error");
          setFeedback(repairMojibakeText(error.response?.data?.error || copy.empty));
        }
      } finally {
        if (isMounted) {
          setIsLoadingCourses(false);
        }
      }
    }

    fetchCourses();
    return () => {
      isMounted = false;
    };
  }, [copy.empty, coursesVersion, user?.id]);

  useEffect(() => {
    let isMounted = true;

    async function fetchAssessments() {
      if (!user?.id || !selectedCourseId) {
        setAssessments([]);
        return;
      }

      try {
        setIsLoadingAssessments(true);
        const response = await api.get(`/courses/${selectedCourseId}/assessments`, {
          params: { trainer_id: user.id }
        });
        if (isMounted) {
          setAssessments(repairMojibakeDeep(response.data.assessments || []));
        }
      } catch (error) {
        if (isMounted) {
          setFeedbackType("error");
          setFeedback(repairMojibakeText(error.response?.data?.error || copy.createError));
        }
      } finally {
        if (isMounted) {
          setIsLoadingAssessments(false);
        }
      }
    }

    fetchAssessments();
    return () => {
      isMounted = false;
    };
  }, [copy.createError, selectedCourseId, user?.id]);

  useEffect(() => {
    let isMounted = true;

    async function fetchQuestionBank() {
      if (!user?.id || !isQuestionBankOpen) {
        return;
      }

      try {
        setIsLoadingQuestionBank(true);
        const response = await api.get(`/trainer/${user.id}/question-bank`);
        if (isMounted) {
          setQuestionBank(repairMojibakeDeep(response.data.questions || []));
        }
      } catch (error) {
        if (isMounted) {
          setFeedbackType("error");
          setFeedback(repairMojibakeText(
            error.response?.data?.error ||
              (isArabic ? "تعذر تحميل بنك الأسئلة الآن" : "Unable to load the question bank right now")
          ));
        }
      } finally {
        if (isMounted) {
          setIsLoadingQuestionBank(false);
        }
      }
    }

    fetchQuestionBank();
    return () => {
      isMounted = false;
    };
  }, [isArabic, isQuestionBankOpen, user?.id]);

  const summary = useMemo(
    () => courses.reduce(
      (accumulator, course) => ({
        totalCourses: accumulator.totalCourses + 1,
        totalAssessments: accumulator.totalAssessments + Number(course.assessments_count || 0),
        totalSubmissions: accumulator.totalSubmissions + Number(course.submissions_count || 0)
      }),
      { totalCourses: 0, totalAssessments: 0, totalSubmissions: 0 }
    ),
    [courses]
  );

  const selectedCourse = useMemo(
    () => courses.find((course) => String(course.id) === String(selectedCourseId)) || null,
    [courses, selectedCourseId]
  );

  const assessmentPairs = useMemo(() => {
    const grouped = new Map();

    assessments.forEach((assessment) => {
      const key = assessment.shared_code || `single-${assessment.id}`;
      const current = grouped.get(key) || {
        shared_code: assessment.shared_code || "",
        title: assessment.title,
        questions_count: assessment.questions_count || 0,
        submissions_count: 0,
        pre: null,
        post: null
      };

      current.title = assessment.title;
      current.questions_count = assessment.questions_count || current.questions_count;
      current.submissions_count += Number(assessment.submissions_count || 0);

      if (assessment.type === "pre") {
        current.pre = assessment;
      }

      if (assessment.type === "post") {
        current.post = assessment;
      }

      grouped.set(key, current);
    });

    return Array.from(grouped.values());
  }, [assessments]);

  const copyText = async (value) => {
    if (!value) {
      return;
    }

    try {
      await window.navigator.clipboard.writeText(value);
      setFeedbackType("success");
      setFeedback(copy.copied);
    } catch (error) {
      setFeedbackType("error");
      setFeedback(isArabic ? "تعذر النسخ" : "Copy failed");
    }
  };

  const handleCourseChange = (field, value) => {
    setCourseForm((current) => ({ ...current, [field]: value }));
  };

  const updateQuestion = (index, field, value) => {
    setCourseForm((current) => ({
      ...current,
      questions: current.questions.map((question, questionIndex) => (
        questionIndex === index
          ? {
            ...question,
            [field]: value,
            ...(field === "question_type" && value === "mcq"
              ? {
                options: question.options?.length
                  ? question.options
                  : [
                    { option_text: "", is_correct: true },
                    { option_text: "", is_correct: false }
                  ]
              }
              : {}),
            ...(field === "question_type" && value !== "mcq" ? { options: [] } : {})
          }
          : question
      ))
    }));
  };

  const addQuestion = () => {
    setCourseForm((current) => ({
      ...current,
      questions: [...current.questions, createQuestionDraft(current.questions.length)]
    }));
  };

  const removeQuestion = (index) => {
    setCourseForm((current) => {
      const nextQuestions = current.questions.filter((_, questionIndex) => questionIndex !== index);
      return {
        ...current,
        questions: nextQuestions.length
          ? nextQuestions.map((question, questionIndex) => ({
            ...question,
            question_order: questionIndex + 1
          }))
          : [createQuestionDraft(0)]
      };
    });
  };

  const addOption = (questionIndex) => {
    setCourseForm((current) => ({
      ...current,
      questions: current.questions.map((question, index) => (
        index === questionIndex
          ? {
            ...question,
            options: [...(question.options || []), { option_text: "", is_correct: false }]
          }
          : question
      ))
    }));
  };

  const updateOption = (questionIndex, optionIndex, field, value) => {
    setCourseForm((current) => ({
      ...current,
      questions: current.questions.map((question, index) => {
        if (index !== questionIndex) {
          return question;
        }

        const nextOptions = (question.options || []).map((option, currentOptionIndex) => {
          if (currentOptionIndex !== optionIndex) {
            return option;
          }

          return { ...option, [field]: value };
        });

        return { ...question, options: nextOptions };
      })
    }));
  };

  const removeOption = (questionIndex, optionIndex) => {
    setCourseForm((current) => ({
      ...current,
      questions: current.questions.map((question, index) => (
        index === questionIndex
          ? {
            ...question,
            options: (question.options || []).filter((_, currentOptionIndex) => currentOptionIndex !== optionIndex)
          }
          : question
      ))
    }));
  };

  const handleCreateFullCourse = async (event) => {
    event.preventDefault();

    if (!user?.id || !courseForm.title.trim() || !courseForm.assessmentTitle.trim()) {
      return;
    }

    try {
      setIsCreatingCourse(true);

      const courseResponse = await api.post("/create_course", {
        title: courseForm.title.trim(),
        description: courseForm.description.trim(),
        trainer_id: user.id
      });

      const createdCourseId = courseResponse.data?.course_id;

      const assessmentResponse = await api.post("/create_assessment", {
        course_id: createdCourseId,
        title: courseForm.assessmentTitle.trim(),
        show_results_to_student: courseForm.show_results_to_student,
        show_answers_to_student: courseForm.show_answers_to_student,
        show_ai_report_to_student: courseForm.show_ai_report_to_student
      });

      const preAssessment = (assessmentResponse.data?.assessments || []).find(
        (assessment) => assessment.type === "pre"
      );

      for (let index = 0; index < courseForm.questions.length; index += 1) {
        const question = courseForm.questions[index];
        const questionText = question.question_text.trim();

        if (!questionText || !preAssessment?.id) {
          continue;
        }

        const payload = {
          assessment_id: preAssessment.id,
          question_text: questionText,
          question_type: question.question_type,
          is_required: true,
          points: Number(question.points) || 1,
          question_order: index + 1
        };

        const options = buildOptionsForQuestion(question);
        if (options.length) {
          payload.options = options;
        }

        await api.post("/add_question", payload);
      }

      setCourseForm(createCourseDraft());
      setSelectedCourseId(String(createdCourseId));
      setCoursesVersion((current) => current + 1);
      setFeedbackType("success");
      setFeedback(`${copy.fullCourseCreated}. ${copy.courseCreatedSelectHint}`);
    } catch (error) {
      setFeedbackType("error");
      setFeedback(repairMojibakeText(error.response?.data?.error || copy.createError));
    } finally {
      setIsCreatingCourse(false);
    }
  };

  const handleDeleteCourse = async (courseId) => {
    if (!user?.id) {
      return;
    }

    const confirmed = window.confirm(copy.deleteCourseConfirm);
    if (!confirmed) {
      return;
    }

    try {
      setDeletingCourseId(courseId);
      await api.delete(`/courses/${courseId}`, {
        params: { trainer_id: user.id }
      });
      setFeedbackType("success");
      setFeedback(copy.deleteSuccess);
      if (String(courseId) === String(selectedCourseId)) {
        setSelectedCourseId("");
        setAssessments([]);
      }
      setCoursesVersion((current) => current + 1);
    } catch (error) {
      setFeedbackType("error");
      setFeedback(repairMojibakeText(error.response?.data?.error || copy.deleteError));
    } finally {
      setDeletingCourseId(null);
    }
  };

  return (
    <div className="workspace-page">
      <section className="hero-panel hero-panel--courses">
        <div>
          <p className="section-eyebrow">{copy.eyebrow}</p>
          <h2 className="section-title">{copy.title}</h2>
          <p className="section-subtitle">{copy.subtitle}</p>
        </div>
        <div className="action-cluster">
          <button className="secondary-button" type="button" onClick={() => navigate("/dashboard")}>
            {copy.openDashboard}
          </button>
          <button className="primary-button" type="button" onClick={() => navigate("/ai-studio")}>
            {copy.openAiStudio}
          </button>
        </div>
      </section>

      {feedback && <div className={`feedback-banner feedback-banner--${feedbackType}`}>{feedback}</div>}

      <section className="metric-grid">
        <article className="metric-card">
          <span>{copy.totalCourses}</span>
          <strong>{summary.totalCourses}</strong>
        </article>
        <article className="metric-card">
          <span>{copy.totalAssessments}</span>
          <strong>{summary.totalAssessments}</strong>
        </article>
        <article className="metric-card">
          <span>{copy.totalSubmissions}</span>
          <strong>{summary.totalSubmissions}</strong>
        </article>
        <article className="metric-card">
          <span>{isArabic ? "بنك الأسئلة" : "Question Bank"}</span>
          <strong>{questionBank.length || (isArabic ? "مكتبة الأسئلة" : "Question Library")}</strong>
          <button
            className="secondary-button"
            type="button"
            onClick={() => setIsQuestionBankOpen((current) => !current)}
            style={{ marginTop: "0.85rem" }}
          >
            {isQuestionBankOpen
              ? (isArabic ? "إخفاء بنك الأسئلة" : "Hide Question Bank")
              : (isArabic ? "فتح بنك الأسئلة" : "Open Question Bank")}
          </button>
        </article>
      </section>

      {isQuestionBankOpen && (
        <section className="panel-card stack-md">
          <div className="card-title-row">
            <div>
              <h3>{isArabic ? "بنك الأسئلة" : "Question Bank"}</h3>
              <p className="muted-text">
                {isArabic
                  ? "مكتبة سريعة لعرض الأسئلة المحفوظة بدون الخروج من صفحة الكورسات."
                  : "A quick library preview for your saved questions without leaving the courses page."}
              </p>
            </div>
          </div>

          {isLoadingQuestionBank ? (
            <div className="empty-state">{isArabic ? "جارٍ تحميل بنك الأسئلة..." : "Loading question bank..."}</div>
          ) : !questionBank.length ? (
            <div className="empty-state">{isArabic ? "لا توجد أسئلة محفوظة في بنك الأسئلة بعد." : "There are no saved question bank items yet."}</div>
          ) : (
            <div className="stack-sm">
              {questionBank.slice(0, 8).map((question) => (
                <article key={question.id} className="course-card">
                  <div className="card-title-row">
                    <strong>{question.question_text}</strong>
                    <span className="status-pill">
                      {question.question_type === "mcq"
                        ? (isArabic ? "اختيار من متعدد" : "Multiple Choice")
                        : question.question_type === "essay"
                          ? (isArabic ? "مقالي" : "Essay")
                          : question.question_type === "true_false"
                            ? (isArabic ? "صح أو خطأ" : "True / False")
                            : (isArabic ? "تقييم" : "Rating")}
                    </span>
                  </div>
                  <div className="course-code-grid">
                    <div className="code-card">
                      <span>{isArabic ? "الدرجة" : "Points"}</span>
                      <strong>{question.points || 1}</strong>
                    </div>
                    <div className="code-card">
                      <span>{isArabic ? "عدد الخيارات" : "Options"}</span>
                      <strong>{Array.isArray(question.options) ? question.options.length : 0}</strong>
                    </div>
                  </div>
                </article>
              ))}

              {questionBank.length > 8 && (
                <div className="muted-text">
                  {isArabic
                    ? `يتم عرض أول 8 أسئلة فقط من أصل ${questionBank.length} سؤالًا.`
                    : `Showing the first 8 questions out of ${questionBank.length}.`}
                </div>
              )}
            </div>
          )}
        </section>
      )}

      <section className="workspace-grid">
        <div className="panel-card">
          <div className="card-title-row">
            <div>
              <h3>{copy.fullCourseTitle}</h3>
              <p className="muted-text">{copy.fullCourseSubtitle}</p>
            </div>
          </div>

          <form className="stack-lg" onSubmit={handleCreateFullCourse}>
            <div className="two-column-grid">
              <label className="field-block">
                <span>{copy.courseTitle}</span>
                <input
                  className="text-input"
                  value={courseForm.title}
                  placeholder={copy.courseTitlePlaceholder}
                  onChange={(event) => handleCourseChange("title", event.target.value)}
                />
              </label>

              <label className="field-block">
                <span>{copy.pairTitle}</span>
                <input
                  className="text-input"
                  value={courseForm.assessmentTitle}
                  placeholder={copy.pairTitlePlaceholder}
                  onChange={(event) => handleCourseChange("assessmentTitle", event.target.value)}
                />
              </label>
            </div>

            <label className="field-block">
              <span>{copy.courseDescription}</span>
              <textarea
                className="text-input"
                rows={3}
                value={courseForm.description}
                placeholder={copy.courseDescriptionPlaceholder}
                onChange={(event) => handleCourseChange("description", event.target.value)}
              />
            </label>

            <div className="action-cluster">
              <label className="checkbox-row">
                <input
                  type="checkbox"
                  checked={courseForm.show_results_to_student}
                  onChange={(event) => handleCourseChange("show_results_to_student", event.target.checked)}
                />
                <span>{copy.showScore}</span>
              </label>

              <label className="checkbox-row">
                <input
                  type="checkbox"
                  checked={courseForm.show_answers_to_student}
                  onChange={(event) => handleCourseChange("show_answers_to_student", event.target.checked)}
                />
                <span>{copy.showAnswers}</span>
              </label>

              <label className="checkbox-row">
                <input
                  type="checkbox"
                  checked={courseForm.show_ai_report_to_student}
                  onChange={(event) => handleCourseChange("show_ai_report_to_student", event.target.checked)}
                />
                <span>{copy.showAi}</span>
              </label>
            </div>

            <div className="stack-lg">
              {courseForm.questions.map((question, questionIndex) => (
                <article key={`question-${questionIndex}`} className="course-card">
                  <div className="card-title-row">
                    <strong>{copy.questionLabel} {questionIndex + 1}</strong>
                    <button className="ghost-button" type="button" onClick={() => removeQuestion(questionIndex)}>
                      {copy.removeQuestion}
                    </button>
                  </div>

                  <div className="two-column-grid">
                    <label className="field-block">
                      <span>{copy.questionText}</span>
                      <textarea
                        className="text-input"
                        rows={3}
                        value={question.question_text}
                        placeholder={copy.questionTextPlaceholder}
                        onChange={(event) => updateQuestion(questionIndex, "question_text", event.target.value)}
                      />
                    </label>

                    <div className="stack-lg">
                      <label className="field-block">
                        <span>{copy.questionType}</span>
                        <select
                          className="text-input"
                          value={question.question_type}
                          onChange={(event) => updateQuestion(questionIndex, "question_type", event.target.value)}
                        >
                          {QUESTION_TYPES.map((type) => (
                            <option key={type} value={type}>
                              {copy[`type_${type}`]}
                            </option>
                          ))}
                        </select>
                      </label>

                      <label className="field-block">
                        <span>{copy.questionPoints}</span>
                        <input
                          className="text-input"
                          type="number"
                          min="1"
                          step="1"
                          value={question.points}
                          onChange={(event) => updateQuestion(questionIndex, "points", event.target.value)}
                        />
                      </label>

                      <label className="checkbox-row">
                        <input checked type="checkbox" readOnly />
                        <span>{copy.questionRequired}</span>
                      </label>
                    </div>
                  </div>

                  {question.question_type === "mcq" ? (
                    <div className="stack-lg">
                      {(question.options || []).map((option, optionIndex) => (
                        <div key={`option-${questionIndex}-${optionIndex}`} className="two-column-grid">
                          <label className="field-block">
                            <span>{copy.optionText}</span>
                            <input
                              className="text-input"
                              value={option.option_text}
                              onChange={(event) => updateOption(questionIndex, optionIndex, "option_text", event.target.value)}
                            />
                          </label>

                          <div className="action-cluster">
                            <label className="checkbox-row">
                              <input
                                type="checkbox"
                                checked={Boolean(option.is_correct)}
                                onChange={(event) => updateOption(questionIndex, optionIndex, "is_correct", event.target.checked)}
                              />
                              <span>{copy.correctOption}</span>
                            </label>
                            <button
                              className="ghost-button"
                              type="button"
                              onClick={() => removeOption(questionIndex, optionIndex)}
                              disabled={(question.options || []).length <= 2}
                            >
                              {copy.removeOption}
                            </button>
                          </div>
                        </div>
                      ))}

                      <div className="action-cluster">
                        <button className="secondary-button" type="button" onClick={() => addOption(questionIndex)}>
                          {copy.addOption}
                        </button>
                      </div>
                    </div>
                  ) : null}

                  {question.question_type === "true_false" ? (
                    <p className="muted-text">{copy.trueFalseHelp}</p>
                  ) : null}

                  {question.question_type === "rating" ? (
                    <p className="muted-text">{copy.ratingHelp}</p>
                  ) : null}
                </article>
              ))}

              <div className="action-cluster">
                <button className="secondary-button" type="button" onClick={addQuestion}>
                  {copy.addQuestion}
                </button>
                <button className="primary-button" type="submit" disabled={isCreatingCourse}>
                  {isCreatingCourse ? copy.creating : copy.createFullCourse}
                </button>
              </div>
            </div>
          </form>
        </div>

        <div className="panel-card">
          <div className="card-title-row">
            <h3>{copy.existingCourses}</h3>
          </div>

          {isLoadingCourses ? (
            <p className="muted-text">{copy.loading}</p>
          ) : courses.length ? (
            <div className="course-stack">
              {courses.map((course) => {
                const isSelected = String(course.id) === String(selectedCourseId);

                return (
                  <article key={course.id} className="course-card">
                    <div className="card-title-row">
                      <div>
                        <strong>{course.title}</strong>
                        {course.description ? <p className="muted-text">{course.description}</p> : null}
                      </div>
                      <span className="status-pill">{isSelected ? copy.selectedCourse : copy.courseDetails}</span>
                    </div>

                    <div className="details-grid">
                      <div className="detail-chip">
                        <span>{copy.assessmentCount}</span>
                        <strong>{course.assessments_count || 0}</strong>
                      </div>
                      <div className="detail-chip">
                        <span>{copy.submissionCount}</span>
                        <strong>{course.submissions_count || 0}</strong>
                      </div>
                    </div>

                    <div className="action-cluster">
                      <button
                        className="ghost-button"
                        type="button"
                        onClick={() => setSelectedCourseId(String(course.id))}
                      >
                        {copy.chooseCourse}
                      </button>
                      <button
                        className="danger-button"
                        type="button"
                        disabled={deletingCourseId === course.id}
                        onClick={() => handleDeleteCourse(course.id)}
                      >
                        {copy.deleteCourse}
                      </button>
                    </div>
                  </article>
                );
              })}
            </div>
          ) : (
            <p className="muted-text">{copy.empty}</p>
          )}
        </div>
      </section>

      <section className="panel-card">
        <div className="two-column-grid">
          <label className="field-block">
            <span>{copy.chooseCourse}</span>
            <select
              className="text-input"
              value={selectedCourseId}
              onChange={(event) => setSelectedCourseId(event.target.value)}
            >
              <option value="">{copy.chooseCourse}</option>
              {courses.map((course) => (
                <option key={course.id} value={course.id}>
                  {course.title}
                </option>
              ))}
            </select>
          </label>

          <div className="detail-chip">
            <span>{copy.selectedCourse}</span>
            <strong>{selectedCourse?.title || "-"}</strong>
          </div>
        </div>
      </section>

      <section className="workspace-grid">
        <div className="panel-card">
          <div className="card-title-row">
            <h3>{copy.assessmentPairs}</h3>
          </div>

          {isLoadingCourses || isLoadingAssessments ? (
            <p className="muted-text">{isLoadingCourses ? copy.loading : copy.loadAssessments}</p>
          ) : !selectedCourseId ? (
            <p className="muted-text">{copy.noCourseSelected}</p>
          ) : assessmentPairs.length ? (
            <div className="course-stack">
              {assessmentPairs.map((pair) => (
                <article key={pair.shared_code || pair.title} className="course-card trainer-rating-card">
                  <div className="card-title-row">
                    <strong>{pair.title}</strong>
                    <span className="status-pill">{copy.viewCodes}</span>
                  </div>

                  <div className="details-grid">
                    <div className="detail-chip">
                      <span>{copy.questions}</span>
                      <strong>{pair.questions_count || 0}</strong>
                    </div>
                    <div className="detail-chip">
                      <span>{copy.submissions}</span>
                      <strong>{pair.submissions_count || 0}</strong>
                    </div>
                  </div>

                  {pair.shared_code ? (
                    <div className="stack-sm">
                      <div className="detail-chip">
                        <span>{copy.baseCode}</span>
                        <strong>{pair.shared_code}</strong>
                      </div>
                      <p className="muted-text">{copy.courseCodeHint}</p>
                      <div className="details-grid">
                        <div className="detail-chip">
                          <span>{copy.preCode}</span>
                          <strong>{pair.pre?.access_code || "-"}</strong>
                        </div>
                        <div className="detail-chip">
                          <span>{copy.postCode}</span>
                          <strong>{pair.post?.access_code || "-"}</strong>
                        </div>
                      </div>
                      <div className="action-cluster">
                        <button className="ghost-button" type="button" onClick={() => copyText(pair.shared_code)}>
                          {copy.copyBaseCode}
                        </button>
                        {pair.pre?.access_code ? (
                          <button className="ghost-button" type="button" onClick={() => copyText(pair.pre.access_code)}>
                            {copy.copyPreCode}
                          </button>
                        ) : null}
                        {pair.post?.access_code ? (
                          <button className="ghost-button" type="button" onClick={() => copyText(pair.post.access_code)}>
                            {copy.copyPostCode}
                          </button>
                        ) : null}
                      </div>
                    </div>
                  ) : null}
                </article>
              ))}
            </div>
          ) : (
            <p className="muted-text">{copy.noAssessments}</p>
          )}
        </div>
      </section>
    </div>
  );
}

export default TrainerCoursesPage;
